import { useState, useCallback } from 'react'
import { useToast } from '@chakra-ui/react'
import apiService from '@/services/api'

interface UseTagManagementReturn {
  isAdding: boolean
  isRemoving: string | null
  addTag: (fileId: number, tagName: string) => Promise<boolean>
  removeTag: (fileId: number, tagName: string) => Promise<boolean>
  addMultipleTags: (fileId: number, tagNames: string[]) => Promise<boolean>
}

export function useTagManagement(): UseTagManagementReturn {
  const [isAdding, setIsAdding] = useState(false)
  const [isRemoving, setIsRemoving] = useState<string | null>(null)
  const toast = useToast()

  const addTag = useCallback(
    async (fileId: number, tagName: string): Promise<boolean> => {
      try {
        setIsAdding(true)
        await apiService.addFileTag(fileId, tagName)

        toast({
          title: 'Tag added',
          description: `Added "${tagName}" successfully`,
          status: 'success',
          duration: 2000,
        })

        return true
      } catch (error: any) {
        toast({
          title: 'Failed to add tag',
          description: error.response?.data?.error || error.message || 'An error occurred',
          status: 'error',
          duration: 3000,
        })
        return false
      } finally {
        setIsAdding(false)
      }
    },
    [toast]
  )

  const removeTag = useCallback(
    async (fileId: number, tagName: string): Promise<boolean> => {
      try {
        setIsRemoving(tagName)
        await apiService.removeFileTag(fileId, tagName)

        toast({
          title: 'Tag removed',
          description: `Removed "${tagName}" successfully`,
          status: 'info',
          duration: 2000,
        })

        return true
      } catch (error: any) {
        toast({
          title: 'Failed to remove tag',
          description: error.response?.data?.error || error.message || 'An error occurred',
          status: 'error',
          duration: 3000,
        })
        return false
      } finally {
        setIsRemoving(null)
      }
    },
    [toast]
  )

  const addMultipleTags = useCallback(
    async (fileId: number, tagNames: string[]): Promise<boolean> => {
      try {
        setIsAdding(true)
        await apiService.addMultipleTagsToFile(fileId, tagNames)

        toast({
          title: 'Tags added',
          description: `Added ${tagNames.length} tags successfully`,
          status: 'success',
          duration: 2000,
        })

        return true
      } catch (error: any) {
        toast({
          title: 'Failed to add tags',
          description: error.response?.data?.error || error.message || 'An error occurred',
          status: 'error',
          duration: 3000,
        })
        return false
      } finally {
        setIsAdding(false)
      }
    },
    [toast]
  )

  return {
    isAdding,
    isRemoving,
    addTag,
    removeTag,
    addMultipleTags,
  }
}
